import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useParams, useSearch} from '@tanstack/react-router'
import services from '../../services'

// Public, chrome-less race results widget for embedding on an organizer's own site. Framed by an
// <iframe> (or injected by /embed.js) — no auth, no app nav, just the results table.
export const Route = createFileRoute('/embed/races/$handle')({
    component:      EmbedRaceWidget,
    validateSearch: (search: Record<string, unknown>): {theme?: 'light' | 'dark'; limit?: number} => ({
        theme: search.theme === 'dark' ? 'dark' : 'light',
        limit: typeof search.limit === 'string' ? parseInt(search.limit, 10) || undefined : undefined,
    }),
    head:           () => ({meta: [{name: 'robots', content: 'noindex'}]}),
})

function EmbedRaceWidget() {
    const {handle} = useParams({strict: false}) as {handle: string};
    const {theme, limit} = useSearch({strict: false}) as {theme?: 'light' | 'dark'; limit?: number};

    const raceQuery = useQuery({
        queryKey: ['embed-race', handle],
        queryFn:  () => services.races.publicRace(handle),
    });

    const dark = theme === 'dark';
    const race = raceQuery.data?.race;
    const all = raceQuery.data?.results || [];
    const results = limit ? all.slice(0, limit) : all;

    const shell = dark ? 'bg-neutral-900 text-white' : 'bg-white text-[#1a1a2e]';
    const rowBorder = dark ? 'border-neutral-700' : 'border-gray-200';
    const muted = dark ? 'text-white/60' : 'text-gray-500';

    if (raceQuery.isLoading) {
        return <div className={`p-4 text-sm ${shell} ${muted}`}>Loading results…</div>;
    }
    if (!race) {
        return <div className={`p-4 text-sm ${shell} ${muted}`}>Race not found.</div>;
    }

    return (
        <div className={`min-h-full p-3 ${shell}`}>
            <div className="mb-2">
                <div className="font-bold">{race.title}</div>
                <div className={`text-xs ${muted}`}>
                    {[race.distance, race.starts_at ? new Date(race.starts_at).toLocaleDateString() : null]
                        .filter(Boolean).join(' · ')}
                </div>
            </div>

            {results.length === 0 ? (
                <div className={`py-6 text-center text-sm ${muted}`}>
                    {race.registration_open ? 'Entries are open — results appear here after the race.' : 'No results yet.'}
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className={`border-b ${rowBorder} ${muted} text-left`}>
                            <th className="py-1 pr-2">#</th>
                            <th className="py-1 pr-2">Name</th>
                            <th className="py-1 pr-2">Time</th>
                            <th className="py-1 text-right">Pts</th>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((r: any) => (
                            <tr key={r.uid} className={`border-b ${rowBorder}`}>
                                <td className="py-1 pr-2 font-semibold">{r.overall_position ?? '-'}</td>
                                <td className="py-1 pr-2">{r.name}</td>
                                <td className="py-1 pr-2 tabular-nums">{r.gun_time || r.chip_time || '-'}</td>
                                <td className="py-1 text-right font-semibold">{r._points}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <div className={`pt-2 text-center text-[10px] ${muted}`}>
                <a href={`https://scorepal.co/race/${race.slug || race.uid}/`} target="_blank" rel="noreferrer" className="underline">
                    Full results on ScorePal
                </a>
            </div>
        </div>
    );
}
